import { Box, ComboboxKeyboardEvents, PopoverBase, Stack, Tooltip } from '@chronosphereio/chrono-ui';
import { produce } from 'immer';
import { Plus as PlusIcon } from 'mdi-material-ui';
import { useState } from 'react';
import { Group } from '@/dashboards/components/Builder';
import { useBuilderContext } from '../promql-builder-context';
import { FILTER_OPERATORS, Filter, isValidFilter } from '../promql-builder-model';
import { FilterEditor } from './FilterEditor';
import { FilterLabelSelector } from './FilterLabelSelector';
import { FilterOperatorSelector } from './FilterOperatorSelector';
import { FilterValueSelector } from './FilterValueSelector';

const { shouldOpenCombobox } = ComboboxKeyboardEvents;

const DEFAULT_FILTER = { label: '', values: [], operator: FILTER_OPERATORS.Equal };

export function FilterSelection() {
  const { filters, onFiltersChange, hasFilters } = useBuilderContext();

  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const openEditor = () => setIsEditorOpen(true);
  const closeEditor = () => setIsEditorOpen(false);

  // State used when adding a filter with the FilterEditor.
  const [newFilter, setNewFilter] = useState<Filter>(DEFAULT_FILTER);

  // When opening the editor, reset to a new filter.
  const handleEditorOpen = (e: React.SyntheticEvent) => {
    e.preventDefault();
    setNewFilter(DEFAULT_FILTER);
    openEditor();
  };

  // When submitting the editor, close editor + update the filters.
  const handleEditorApply = () => {
    closeEditor();
    handleFilterAdd(newFilter);
  };

  // When canceling the editor, close editor.
  const handleEditorCancel = () => {
    closeEditor();
  };

  // When clicking outside the editor, either submit or cancel.
  const handleEditorClickAway = () => {
    if (isValidFilter(newFilter)) {
      handleEditorApply();
    } else {
      handleEditorCancel();
    }
  };

  const handleFilterAdd = (filter: Filter) => {
    onFiltersChange(
      produce(filters, (draft) => {
        draft.push(filter);
      })
    );
  };

  const handleFilterChange = (filter: Filter, index: number) => {
    onFiltersChange(
      produce(filters, (draft) => {
        draft[index] = filter;
      })
    );
  };

  const handleFilterDelete = (index: number) => {
    onFiltersChange(
      produce(filters, (draft) => {
        draft.splice(index, 1);
      })
    );
  };

  return (
    <>
      {!hasFilters ? (
        <Group.Root>
          <Group.Label>Filters</Group.Label>
          <PopoverBase
            open={isEditorOpen}
            onClose={handleEditorClickAway}
            Content={
              <FilterEditor
                filter={newFilter}
                onFilterChange={setNewFilter}
                onApply={handleEditorApply}
                onCancel={handleEditorCancel}
              />
            }
          >
            {/* Box makes sure that Popover has a stable anchor element */}
            <Box sx={{ height: '100%' }}>
              <Group.Button
                isLast
                isPlaceholder
                onClick={handleEditorOpen}
                onKeyDown={(e) => {
                  if (shouldOpenCombobox(e)) {
                    handleEditorOpen(e);
                  }
                }}
              >
                No filters
              </Group.Button>
            </Box>
          </PopoverBase>
        </Group.Root>
      ) : (
        <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
          {filters.map((f, index) => (
            <FilterPill
              key={index}
              filter={f}
              onFilterDelete={() => handleFilterDelete(index)}
              onFilterChange={(filter) => handleFilterChange(filter, index)}
            />
          ))}
          <PopoverBase
            open={isEditorOpen}
            onClose={handleEditorClickAway}
            Content={
              <FilterEditor
                filter={newFilter}
                onFilterChange={setNewFilter}
                onApply={handleEditorApply}
                onCancel={handleEditorCancel}
              />
            }
          >
            {/* Box makes sure that Popover has a stable anchor element */}
            <Box sx={{ height: '100%' }}>
              <Tooltip Content="Add filter">
                <span>
                  <Group.Root>
                    <Group.Button
                      isSolo
                      onClick={handleEditorOpen}
                      onKeyDown={(e) => {
                        if (shouldOpenCombobox(e)) {
                          handleEditorOpen(e);
                        }
                      }}
                    >
                      <PlusIcon fontSize="inherit" />
                    </Group.Button>
                  </Group.Root>
                </span>
              </Tooltip>
            </Box>
          </PopoverBase>
        </Stack>
      )}
    </>
  );
}

function FilterPill({
  filter,
  onFilterDelete,
  onFilterChange,
}: {
  filter: Filter;
  onFilterDelete: () => void;
  onFilterChange: (filter: Filter) => void;
}) {
  return (
    <Group.Root onRemove={onFilterDelete}>
      <Group.Label>Filter</Group.Label>
      <FilterLabelSelector filter={filter} onFilterChange={onFilterChange} />
      <FilterOperatorSelector filter={filter} onFilterChange={onFilterChange} />
      <FilterValueSelector filter={filter} onFilterChange={onFilterChange} />
    </Group.Root>
  );
}
